import { Box, Grid } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getAssignedClient,
  getAssignedClientContact,
  revokeClientUser,
  revokeClientContact,
  getTeamsByUser,
  deleteTeamMember,
  ClientDerivedDTO,
  ClientShareDTO,
  ClientContactUserDTO,
  ClientContactShareDTO,
  TeamDTO,
  LoginDTO,
  getJobBoardSessionById,
} from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsButton,
  WsDataTable,
  ScrollableTabs,
  EmptyTeamsAndUsersIcon,
  WsListingLoader,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { UserAccessAction, useUserAccessAuthz } from '../../../hooks/useUserAccessAuthz';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import UserAccess from './UserAccess';

export interface IUserActionTableProps {
  email: string;
  moduleName?: string;
  closeDrawer: () => void;
  refeshCount: () => void;
}

const UserActionTable = ({
  email,
  moduleName,
  closeDrawer,
  refeshCount,
}: IUserActionTableProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const workspace = useWorkspaceContext();
  const [clientDetails, setClientDetails] = useState<ClientDerivedDTO[]>([]);
  const [clientContactDetails, setClientContactDetails] = useState<ClientContactUserDTO[]>([]);
  const [teamDetails, setTeamDetails] = useState<TeamDTO[]>([]);
  const [tabPanelPage, setTabPanelPage] = useState(0);
  const [loginDetails, setLoginDetails] = useState<LoginDTO | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const { access: userAccessControl } = useUserAccessAuthz([UserAccessAction.MANAGE_USERS_ACCESS]);

  const manageAccess = workspace.owns || userAccessControl[0];

  useEffect(() => {
    const loadUserDetailsByModule = async () => {
      if (tabPanelPage == 1) {
        setLoader(true);
        const response = await getAssignedClient(email);
        setClientDetails(response.data);
        setLoader(false);
      }
      if (tabPanelPage == 2) {
        setLoader(true);
        const response = await getAssignedClientContact(email);
        setClientContactDetails(response.data);
        setLoader(false);
      }
      if (tabPanelPage == 0) {
        setLoader(true);
        const response = await getTeamsByUser(email);
        setTeamDetails(response.data);
        setLoader(false);
      }
      if (moduleName == 'USER_SESSION') {
        const response = await getJobBoardSessionById(email);
        setLoginDetails(response.data);
      }
    };
    loadUserDetailsByModule();
  }, [email, moduleName, tabPanelPage]);

  const revokeUserAccess = async (email: string, id: string, clientContactId: string) => {
    try {
      let response;
      if (tabPanelPage == 1) {
        const clientShareDTO: ClientShareDTO = {
          clientId: id,
          userList: [email],
        };
        response = await revokeClientUser(clientShareDTO);
      }
      if (tabPanelPage == 2) {
        const clientShareDTO: ClientContactShareDTO = {
          clientId: id,
          clientContactId: clientContactId ?? '',
          userList: [email],
        };
        response = await revokeClientContact(clientShareDTO);
      }
      if (tabPanelPage == 0) {
        response = await deleteTeamMember(email, id);
      }
      if (response.status === 200) {
        setNotification(success('User unassigned Successfully'));
        refeshCount();
      }
      closeDrawer();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const gridStyle = {
    padding: '2em',
  };

  const clientClientContactsHeader = [
    { displayName: 'Client Name', span: 4 },
    { displayName: 'Client Contact Name', span: 8 },
  ];

  const teamsHeader = [{ displayName: 'Teams Name', span: 12 }];

  const loginHeader = [
    { Header: `${loginDetails?.createdBy + ' (Session creator)' ?? ''}`, accessor: 'activeLogin' },
  ];

  const clientsHeader = [{ displayName: 'Client Name', span: 12 }];

  const activeLoginInfo = useMemo(
    () =>
      loginDetails &&
      loginDetails.users.map((list) => ({
        activeLogin: list,
      })),
    [loginDetails]
  );

  const tabList = [
    { tabName: 'Teams', tabCount: teamDetails?.length ?? 0 },
    {
      tabName: 'Clients',
      tabCount: clientDetails?.length ?? 0,
    },
    {
      tabName: 'Clients Contact',
      tabCount: clientContactDetails?.length ?? 0,
    },
    {
      tabName: 'Data Access',
    },
  ];

  return (
    <>
      <FormProvider {...methods}>
        <ScrollableTabs
          scrollList={tabList}
          getTabIndex={setTabPanelPage}
          defaultValue={tabPanelPage}
        />
        {tabPanelPage == 0 && (
          <>
            {teamDetails && teamDetails.length > 0 && !loader ? (
              <Grid container direction="column" style={gridStyle} spacing={2}>
                <Grid item>
                  <Grid container>
                    {teamsHeader.map((header, indexHeader) => (
                      <Grid item key={indexHeader} xs={header.span}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                          {header.displayName}
                        </WsTypography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                {teamDetails.map((team, index) => (
                  <Grid item key={index}>
                    <Grid container>
                      <Grid item xs={8}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                          {team.name}
                        </WsTypography>
                      </Grid>
                      {manageAccess && (
                        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'right' }}>
                          <WsButton
                            variant="contained"
                            size="large"
                            onClick={() => revokeUserAccess(email, team.id, '')}
                          >
                            Revoke
                          </WsButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard
                text="No Teams found"
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={<Box m={2}>{}</Box>}
              />
            )}
          </>
        )}
        {moduleName == 'USER_SESSION' && (
          <Grid container direction="column" style={gridStyle} spacing={2} item xs={15}>
            <WsDataTable columns={loginHeader} data={activeLoginInfo ?? []} />
          </Grid>
        )}
        {tabPanelPage == 1 && (
          <>
            {clientDetails && clientDetails.length > 0 && !loader ? (
              <Grid container direction="column" style={gridStyle} spacing={2}>
                <Grid item>
                  <Grid container>
                    {clientsHeader.map((header, indexHeader) => (
                      <Grid item key={indexHeader} xs={header.span}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                          {header.displayName}
                        </WsTypography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                {clientDetails.map((client, index) => (
                  <Grid item key={index}>
                    <Grid container>
                      <Grid item xs={8}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                          {client.name}
                        </WsTypography>
                      </Grid>
                      {manageAccess && (
                        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'right' }}>
                          <WsButton
                            variant="contained"
                            size="large"
                            onClick={() => revokeUserAccess(email, client.id, '')}
                          >
                            Revoke
                          </WsButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard
                text="No Clients found"
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={<Box m={2}>{}</Box>}
              />
            )}
          </>
        )}
        {tabPanelPage == 2 && (
          <>
            {clientContactDetails && clientContactDetails.length > 0 && !loader ? (
              <Grid container direction="column" style={gridStyle} spacing={2}>
                <Grid item>
                  <Grid container>
                    {clientClientContactsHeader.map((header, indexHeader) => (
                      <Grid item key={indexHeader} xs={header.span}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                          {header.displayName}
                        </WsTypography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {clientContactDetails.map((clientContact, index) => (
                  <Grid item key={index}>
                    <Grid container justifyContent="flex-end">
                      <Grid item xs={4}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                          {clientContact.clientName}
                        </WsTypography>
                      </Grid>
                      <Grid item xs={4}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                          {clientContact.firstName} {clientContact.lastName}
                        </WsTypography>
                      </Grid>
                      {manageAccess && (
                        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'right' }}>
                          <WsButton
                            variant="contained"
                            size="large"
                            onClick={() =>
                              revokeUserAccess(email, clientContact.clientId, clientContact.id)
                            }
                          >
                            Revoke
                          </WsButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard
                text="No Clients Contacts found"
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={<Box m={2}>{}</Box>}
              />
            )}
          </>
        )}
        {tabPanelPage == 3 && <UserAccess email={email} />}
      </FormProvider>
    </>
  );
};

export default UserActionTable;
